import * as React from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Breadcrumbs, { breadcrumbsClasses } from '@mui/material/Breadcrumbs';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';

const StyledBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
    margin: theme.spacing(1, 0),
    [`& .${breadcrumbsClasses.separator}`]: {
        color: (theme.vars || theme).palette.action.disabled,
        margin: 1,
    },
    [`& .${breadcrumbsClasses.ol}`]: {
        alignItems: 'center',
    },
}));

export default function NavbarBreadcrumbs() {
    const location = useLocation();

    // Dashboard is the root (no parent); all other app pages are one level under it
    const breadcrumbMap = {
        '/dashboard': { label: 'Dashboard', parent: null },
        '/chat': { label: 'Chat', parent: 'Dashboard' },
        '/analytics': { label: 'Analytics', parent: 'Dashboard' },
        '/document-management': { label: 'Documents', parent: 'Dashboard' },
        '/user-management': { label: 'User Management', parent: 'Dashboard' },
        '/company-management': {
            label: 'Company Management',
            parent: 'Dashboard',
        },
        '/activity-logs': { label: 'Activity Logs', parent: 'Dashboard' },
        '/upload': { label: 'Upload', parent: 'Dashboard' },
        '/editor': { label: 'Editor', parent: 'Dashboard' },
        '/preferences': { label: 'Preferences', parent: 'Dashboard' },
        '/signin': { label: 'Sign In', parent: null },
        '/signup': { label: 'Sign Up', parent: null },
    };

    const getBreadcrumbs = () => {
        const currentPath = location.pathname;
        const currentPage = breadcrumbMap[currentPath];

        if (!currentPage) {
            // Fallback for unknown routes
            return [
                <Typography key="home" variant="body1">
                    Home
                </Typography>,
                <Typography
                    key="current"
                    variant="body1"
                    sx={{ color: 'text.primary', fontWeight: 600 }}
                >
                    {currentPath.replace('/', '') || 'Page'}
                </Typography>,
            ];
        }

        const breadcrumbs = [];

        const hasParent =
            currentPage.parent != null && String(currentPage.parent) !== 'null';

        // Add parent if exists (Dashboard links to /dashboard)
        if (hasParent) {
            const parentLabel = currentPage.parent;
            const parentHref =
                parentLabel === 'Dashboard' ? '/dashboard' : null;
            breadcrumbs.push(
                parentHref ? (
                    <Link
                        key="parent"
                        component={RouterLink}
                        to={parentHref}
                        variant="body1"
                        color="inherit"
                        underline="hover"
                    >
                        {parentLabel}
                    </Link>
                ) : (
                    <Typography key="parent" variant="body1">
                        {parentLabel}
                    </Typography>
                )
            );
        }

        // Add current page
        breadcrumbs.push(
            <Typography
                key="current"
                variant="body1"
                sx={{ color: 'text.primary', fontWeight: 600 }}
            >
                {currentPage.label}
            </Typography>
        );

        return breadcrumbs;
    };

    return (
        <StyledBreadcrumbs
            aria-label="breadcrumb"
            separator={<NavigateNextRoundedIcon fontSize="small" />}
            sx={{ paddingLeft: 2 }}
        >
            {getBreadcrumbs()}
        </StyledBreadcrumbs>
    );
}
